import {
	isValidPhoneNumber,
	parsePhoneNumber,
} from "react-phone-number-input/max"
import { z } from "zod"
import {
	CheckboxConfig,
	DateInputConfig,
	DropdownConfig,
	EmailInputConfig,
	FileUploadConfig,
	NumberInputConfig,
	PhoneNumberInputConfig,
	RadioConfig,
	TextAreaConfig,
	TextInputConfig,
	type FIELD,
	type STAGE,
} from "../types/form-types"
import { parseAttributes } from "./utils"

function generateFieldSchema(field: FIELD) {
	const { field_type } = field
	let schema
	let defaultValue: unknown = ""
	let isOTPVerificationRequired = false

	switch (field_type) {
		case "TEXT":
		case "ADDRESS":
			schema = getTextInputFieldSchema(field)
			break

		case "NUMBER":
		case "POSTAL_CODE":
		case "DEB_ID":
			schema = getNumberInputFieldSchema(field)
			// defaultValue = 0
			break

		case "EMAIL": {
			const {
				schema: emailSchema,
				isOTPVerificationRequired: isEmailOTPVerificationRequried,
			} = getEmailInputFieldSchema(field)
			schema = emailSchema
			isOTPVerificationRequired = isEmailOTPVerificationRequried
			break
		}

		case "RADIO": {
			const { schema: radioSchema, defaultValue: radioDefault } =
				getRadioInputFieldSchema(field)
			schema = radioSchema
			defaultValue = radioDefault
			break
		}

		case "DATE":
			schema = generateDateInputFieldSchema(field)
			break

		case "CHECKBOX": {
			const { schema: sch, defaultValue: df } =
				getCheckboxInputFieldSchema(field)
			schema = sch
			defaultValue = df
			break
		}

		case "FILE":
			schema = getFileUploadInputFieldSchema(field)
			defaultValue = []
			break

		case "AADHAR":
			schema = getAadharFieldInputSchema()
			break

		case "TEL": {
			const {
				schema: phoneNumberSchema,
				isOTPVerificationRequired: isPhoneOTPVerificationRequired,
			} = getPhoneNumberInputFieldSchema(field)
			schema = phoneNumberSchema
			isOTPVerificationRequired = isPhoneOTPVerificationRequired
			break
		}

		case "TEXT_AREA":
			schema = getTextAreaInputFieldSchema(field)
			break

		case "PROGRAM_TYPE":
		case "PROGRAM":
		case "SPECIALIZATION":
		case "SELECT": {
			const { schema: selectSchema, defaultValue: selectDefault } =
				getSelectInputFieldSchema(field)
			schema = selectSchema
			defaultValue = selectDefault
			break
		}
		case "MULTI_SELECT": {
			const {
				schema: multiSelectSchema,
				defaultValue: multiSelectDefault,
			} = getMultiSelectInputFieldSchema(field)
			schema = multiSelectSchema
			defaultValue = multiSelectDefault
			break
		}
		case "COUNTRY": {
			const { schema: countrySchema, defaultValue: countryDefault } =
				getSelectInputFieldSchema(field)
			schema = countrySchema
			defaultValue = countryDefault
			break
		}
		case "STATE": {
			const { schema: stateSchema, defaultValue: stateDefault } =
				getSelectInputFieldSchema(field)
			schema = stateSchema
			defaultValue = stateDefault
			break
		}
		case "PAYMENT_ONE": {
			schema = getPaymentOneFieldSchema()
			break
		}
		case "PAYMENT_TWO": {
			schema = getPaymentFieldSchema()
			break
		}
		case "ACKNOWLEDGEMENT": {
			const { schema: fieldSchema, defaultValue: fieldDefault } =
				getAknowledgementFieldSchema(field)
			defaultValue = fieldDefault
			schema = fieldSchema
			break
		}
		default:
			schema = z.any()
	}

	if (!field.is_required) {
		schema = z.preprocess(
			val => (val === "" ? undefined : val),
			schema.optional(),
		)
	} else {
		schema = schema.optional()
	}
	schema = applyGlobalError(schema, field)

	return { schema, defaultValue, isOTPVerificationRequired }
}

function applyGlobalError<T extends z.ZodTypeAny>(base: T, field: FIELD): T {
	const attrs = parseAttributes(field.attributes)
	const globalError = attrs?.error as string | undefined
	if (!globalError) return base

	// Wrap `base` so we keep its parsing/coercions but override messages
	const wrapped = z.any().transform((val, ctx) => {
		const res = base.safeParse(val)
		if (res.success) return res.data // preserve parsed value (incl. coerce)
		ctx.addIssue({ code: z.ZodIssueCode.custom, message: globalError })
		return z.NEVER // signal failure with our single message
	})

	return wrapped as unknown as T // keep T typing at call sites
}
export function generateStageSchema(stage: STAGE) {
	const fields = stage.sections.flatMap(section =>
		section.rows.flatMap(row => row.columns.flatMap(col => col.fields)),
	)

	const shape: Record<string, z.ZodTypeAny> = {}
	const defaults: Record<string, unknown> = {}
	const otpVerificationMap: Record<string, boolean> = {}

	fields.forEach(field => {
		const { schema, defaultValue, isOTPVerificationRequired } =
			generateFieldSchema(field)
		shape[field.field_id] = schema
		if (defaultValue !== undefined) {
			defaults[field.field_id] = defaultValue
		}

		if (isOTPVerificationRequired) {
			otpVerificationMap[field.field_id] = false // init as not verified
		}
	})

	return {
		schema: z.object(shape),
		defaultValues: defaults,
		otpVerificationMap,
	}
}

// functions related to TEXT field
function getTextInputFieldSchema(field: FIELD) {
	let baseString = z.string({
		required_error: "Required field",
		invalid_type_error: "Only text is accepted",
	})

	// If required → enforce non-empty
	if (field.is_required) {
		baseString = baseString.min(1, "Required field")
	}

	const parsedAttributes = parseAttributes(field.attributes)

	// Handle attributes
	Object.entries(parsedAttributes).forEach(([key, value]) => {
		switch (key) {
			case TextInputConfig.CharacterLimit:
				baseString = baseString.max(
					value as number,
					`Only ${value} characters are allowed`,
				)
				break

			case TextInputConfig.InputType: {
				const pattern = getPatternForInputType(value as string)
				baseString = baseString.regex(pattern.pattern, {
					message: pattern.title,
				})
				break
			}
		}
	})

	// Wrap with preprocess (only numbers → string)
	const schema = z.preprocess(
		val => (typeof val === "number" ? String(val) : val),
		baseString,
	)

	return schema
}

function getPatternForInputType(inputType: string) {
	let { pattern, title }: { pattern: RegExp; title: string } = {
		pattern: /[*]/,
		title: "",
	}

	switch (inputType) {
		case "ALPHABET":
			pattern = /^[A-Za-z\s]+$/
			title = "Alphabets only"
			break
		case "ALPHANUMERIC":
			pattern = /^[A-Za-z0-9\s]+$/ // ← added \s here
			title = "Alphabets, numbers, and spaces only"
			break
		default:
			pattern = /^.*$/
			title = ""
			break
	}

	return { pattern, title }
}

// functions related to NUMBER field
function getNumberInputFieldSchema(field: FIELD) {
	let schema = z.coerce.number({
		invalid_type_error: "only numbers are accepted",
	})

	// if (field.is_required) {
	// 	schema = schema.min(1, { message: "Required Field" })
	// }
	const parsedAttributes = parseAttributes(field.attributes)

	Object.entries(parsedAttributes).forEach(([key, value]) => {
		switch (key) {
			case NumberInputConfig.MinValue:
				schema = schema.min(
					value as number,
					`value should be minimum ${value}`,
				)
				break
			case NumberInputConfig.MaxValue: {
				const maxValue = value as number
				if (maxValue > 0) {
					schema = schema.max(
						value as number,
						`value should not be greater than ${value}`,
					)
				}
				break
			}
			case NumberInputConfig.DecimalAllowed:
				if (!value as boolean)
					schema = schema.int("Only whole numbers are allowed")
				break
		}
	})

	return schema
}

//Email field functions
function getEmailInputFieldSchema(field: FIELD) {
	let schema: z.ZodTypeAny = z.string().email("Invalid email address")
	let isOTPVerificationRequired = false

	if (field.is_required) {
		schema = schema.refine(val => val.length > 0, {
			message: "Required Field",
		})
	}

	const parsedAttributes = parseAttributes(field.attributes)

	Object.entries(parsedAttributes).forEach(([key, value]) => {
		switch (key) {
			case EmailInputConfig.DomainRestriction: {
				const allowedDomains: string[] = value as string[]
				if (allowedDomains.length > 0) {
					schema = schema.refine(
						val => {
							if (typeof val !== "string") return true
							const domain = val.split("@")[1]
							return allowedDomains.includes(domain)
						},
						{
							message: `Email must belong to: ${allowedDomains.join(", ")}`,
						},
					)
				}
				break
			}
			case EmailInputConfig.ValidateWithOTP: {
				if (value as boolean) {
					isOTPVerificationRequired = true
				}
			}
		}
	})
	return { schema, isOTPVerificationRequired }
}

//Date field functions
function generateDateInputFieldSchema(field: FIELD) {
	let schema = z.coerce.date({
		errorMap: err => ({ message: `Invalid date ${err.code}` }),
	})

	const parsedAttributes = parseAttributes(field.attributes)

	Object.entries(parsedAttributes).forEach(([key, value]) => {
		switch (key) {
			case DateInputConfig.MinDate: {
				const minDate = new Date(value as Date)
				schema = schema.min(minDate, {
					message: `Date should not be before ${value}`,
				})
				break
			}
			case DateInputConfig.MaxDate: {
				const maxDate = new Date(value as Date)
				schema = schema.max(maxDate, {
					message: `Date should not be after ${value}`,
				})
				break
			}
			// case DateInputConfig.DateFormat:
			//     const format = value as string
			//     schema = schema.refine(
			//         (val) => {
			//             if (!(val instanceof Date) || isNaN(val.getTime())) return false
			//             // Try parsing original input string with date-fns
			//             const parsed = parse(val.toISOString().split("T")[0], format, new Date())
			//             return !isNaN(parsed.getTime())
			//         },
			//         { message: `Date must match format ${value}` }
			//     )
			//     break
		}
	})

	return schema
}

//Radio field functions
function getRadioInputFieldSchema(field: FIELD) {
	let schema: z.ZodString = z.string()
	let defaultValue: string = ""

	const parsedAttributes = parseAttributes(field.attributes)
	if (field.is_required) {
		schema = schema.min(1, { message: "Required Field" })
	}

	Object.entries(parsedAttributes).forEach(([key, value]) => {
		switch (key) {
			// case RadioConfig.Options: {
			// 	const allowedOptions = value as {
			// 		label: string
			// 		value: string
			// 	}[]
			// 	const values: string[] = allowedOptions.map(
			// 		option => option.value,
			// 	)

			// 	if (values.length > 0) {
			// 		baseSchema = z.enum(values as [string, ...string[]], {
			// 			required_error: "This field is required",
			// 		})
			// 	} else {
			// 		baseSchema = z
			// 			.string({
			// 				required_error: "This field is required",
			// 			})
			// 			.min(1, { message: "Required field" })
			// 	}
			// 	break
			// }

			case RadioConfig.DefaultOption:
				defaultValue = value as string
				break
		}
	})

	return { schema, defaultValue }
}

//checkbox field functions
function getCheckboxInputFieldSchema(field: FIELD) {
	let baseSchema: z.ZodArray<z.ZodString> = z.array(z.string())

	let defaultValue: string[] = []

	if (field.is_required) {
		baseSchema = baseSchema.min(1, { message: "Required Field" })
	}

	const parsedAttributes = parseAttributes(field.attributes)

	Object.entries(parsedAttributes).forEach(([key, value]) => {
		switch (key) {
			case CheckboxConfig.DefaultOptions:
				defaultValue = value as string[]
				break
		}
	})

	const schema = z.preprocess(val => {
		if (val === "" || val == null || val == undefined) return []
		// If backend gives a single string → wrap in array
		if (typeof val === "string") return [val]
		return val
	}, baseSchema)

	return { schema, defaultValue }
}

//file upload field functions
function getFileUploadInputFieldSchema(field: FIELD) {
	let schema: z.ZodTypeAny = z.union([
		// Case 1: actual files selected in the input
		z.array(z.instanceof(File)),

		// Case 2: prefilled strings (filenames or URLs from backend)
		z.array(z.string()),
	])

	// If required
	if (field.is_required) {
		schema = schema.refine(val => val.length > 0, {
			message: "Please upload at least one file",
		})
	}

	const parsedAttributes = parseAttributes(field.attributes)
	let maxFileSizeMB: number | undefined = undefined

	Object.entries(parsedAttributes).forEach(([key, value]) => {
		switch (key) {
			case FileUploadConfig.MaxFileSizeMB:
				maxFileSizeMB = value as number
				schema = schema.refine(
					(files: (File | string)[]) => {
						// only validate size if actual File objects
						return files.every(
							f =>
								typeof f === "string" ||
								f.size <= maxFileSizeMB! * 1024 * 1024,
						)
					},
					{ message: `Each file must be <= ${maxFileSizeMB}MB` },
				)
				break
		}
	})

	schema = z.preprocess(val => {
		if (val === "" || val == null) return []
		// If backend gives a single string → wrap in array
		if (typeof val === "string") return [val]
		return val
	}, schema)

	schema = schema.transform(val => {
		//rethink this logic when handling mutliple files in a single file field
		if (Array.isArray(val) && val.every(v => typeof v === "string")) {
			return val[0]
		}

		return val
	})

	return schema
}

//aadhaar field functiona
function getAadharFieldInputSchema() {
	const baseSchema: z.ZodTypeAny = z.string().regex(/^[2-9][0-9]{11}$/, {
		message: "only 12 digits are allowed(should not start with 0 or 1)",
	})

	// Wrap with preprocess (only numbers → string)
	const schema = z.preprocess(
		val => (typeof val === "number" ? String(val) : val),
		baseSchema,
	)

	return schema
}

//phone number field functions

function getPhoneNumberInputFieldSchema(field: FIELD) {
	let schema: z.ZodTypeAny = z.string()
	const parsedAttributes = parseAttributes(field.attributes)
	let isOTPVerificationRequired = false

	if (field.is_required) {
		schema = schema.refine(val => val.length > 0, {
			message: "Required Field",
		})
		schema = schema.refine(val => !val || isValidPhoneNumber(val), {
			message: "Invalid phone number",
		})
	}

	Object.entries(parsedAttributes).forEach(([key, value]) => {
		switch (key) {
			case PhoneNumberInputConfig.ValidateWithOTP: {
				if (value as boolean) {
					isOTPVerificationRequired = true
				}
			}
		}
	})

	schema = schema.transform(val => {
		try {
			const parsed = parsePhoneNumber(val)
			return `+${parsed?.countryCallingCode} ${parsed?.nationalNumber}`
		} catch {
			return val
		}
	})

	return { schema, isOTPVerificationRequired }
}

//text area field function
function getTextAreaInputFieldSchema(field: FIELD) {
	let schema: z.ZodString = z.string()

	if (field.is_required) {
		schema = schema.min(1, { message: "Required Field" })
	}

	const parsedAttributes = parseAttributes(field.attributes)

	Object.entries(parsedAttributes).forEach(([key, value]) => {
		switch (key) {
			case TextAreaConfig.CharacterLimitMin:
				schema = schema.min(
					value as number,
					`minimum ${value} characters are required`,
				)
				break
			case TextAreaConfig.CharacterLimitMax: {
				const maxCharaters = value as number
				if (maxCharaters > 0) {
					schema = schema.max(
						value as number,
						`only ${value} characters are allowed`,
					)
				}
				break
			}
		}
	})

	return schema
}

//select field function
function getSelectInputFieldSchema(field: FIELD) {
	let schema: z.ZodString = z.string({
		required_error: "You have to select at least one item.",
	})

	if (field.is_required) {
		schema = schema.min(1, { message: "Required Field" })
	}

	let defaultValue: string = field.field_type === "COUNTRY" ? "India" : ""

	const parsedAttributes = parseAttributes(field.attributes)

	Object.entries(parsedAttributes).forEach(([key, value]) => {
		switch (key) {
			case DropdownConfig.DefaultSelection:
				defaultValue = value as string
				break
		}
	})

	return { schema, defaultValue }
}

//multi-select field function
function getMultiSelectInputFieldSchema(field: FIELD) {
	let schema: z.ZodString = z.string({
		required_error: "You have to select at least one item.",
	})

	let defaultValue: string = ""

	if (field.is_required) {
		schema = schema.min(1, { message: "Required Field" })
	}

	const parsedAttributes = parseAttributes(field.attributes)

	Object.entries(parsedAttributes).forEach(([key, value]) => {
		switch (key) {
			case DropdownConfig.DefaultSelection:
				defaultValue = value as string
				break
		}
	})

	return { schema, defaultValue }
}

//payment-one field function
//payment-one field function
function getPaymentOneFieldSchema() {
	return z.preprocess(
		val => {
			if (val === undefined || val === "") return {}
			else return val
		},
		z
			.object({
				orderId: z.string().optional(),
				transactionId: z.string().optional(),
			})
			.refine(
				val => {
					if (
						val === undefined ||
						val.orderId === undefined ||
						val.orderId === "" ||
						val.transactionId === undefined ||
						val.transactionId === ""
					) {
						return false
					} else return true
				},
				{
					message:
						"Make Payment to proceed further in the application",
				},
			),
	)
}

//payment-one field function
function getPaymentFieldSchema() {
	return z.preprocess(
		val => {
			if (val === undefined || val === "") return {}
			else return val
		},
		z
			.object({
				payment_success: z.boolean().optional().default(false),
			})
			.superRefine((data, ctx) => {
				if (!data.payment_success) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message:
							"Make Payment to proceed further in the application",
					})
				}
			}),
	)
}

function getAknowledgementFieldSchema(field: FIELD) {
	let schema: z.ZodTypeAny = z.string()
	let defaultValue: string = "TRUE"

	if (field.is_required) {
		defaultValue = "TRUE"
		schema = schema.refine(
			value => {
				return value === "TRUE"
			},
			{
				message: "Required Field",
			},
		)
	}

	return { schema, defaultValue }
}
